import type { GameState } from "./types";
import { migrateGame } from "./storage";
import type { Tournament } from "./tournament";

const DB_NAME = "bullseye-local-v1";
const DB_VERSION = 1;
const LEGACY_HISTORY_KEY = "bullseye.history.v1";

export interface LocalProfile {
  id: string;
  name: string;
  normalizedName: string;
  color: string;
  createdAt: number;
  updatedAt: number;
  archived?: boolean;
}

export interface ArchivedMatch {
  id: string;
  game: GameState;
  profileIds: Record<string, string>;
  archivedAt: number;
  tournamentId?: string;
  tournamentMatchId?: string;
}

const normalizeName = (value: string) =>
  value.trim().replace(/\s+/g, " ").toLocaleLowerCase("it");

const requestValue = <T>(request: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionDone = (transaction: IDBTransaction) =>
  new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });

export function openBullseyeDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains("profiles")) {
        const profiles = database.createObjectStore("profiles", { keyPath: "id" });
        profiles.createIndex("normalizedName", "normalizedName", { unique: false });
      }
      if (!database.objectStoreNames.contains("matches")) {
        const matches = database.createObjectStore("matches", { keyPath: "id" });
        matches.createIndex("archivedAt", "archivedAt", { unique: false });
      }
      if (!database.objectStoreNames.contains("tournaments")) {
        database.createObjectStore("tournaments", { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains("metadata")) {
        database.createObjectStore("metadata", { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function archiveCompletedMatch(
  game: GameState,
  links?: { tournamentId?: string; tournamentMatchId?: string },
): Promise<ArchivedMatch> {
  const database = await openBullseyeDb();
  const transaction = database.transaction(["profiles", "matches"], "readwrite");
  const profileStore = transaction.objectStore("profiles");
  const matchStore = transaction.objectStore("matches");
  const id = `match-${game.createdAt}`;
  const existing = await requestValue(matchStore.get(id)) as ArchivedMatch | undefined;
  if (existing) {
    database.close();
    return existing;
  }

  const profiles = await requestValue(profileStore.getAll()) as LocalProfile[];
  const profileIds: Record<string, string> = {};
  const now = Date.now();
  for (const player of game.players) {
    const normalizedName = normalizeName(player.name);
    let profile = profiles.find((item) => item.normalizedName === normalizedName && !item.archived);
    if (!profile) {
      profile = {
        id: crypto.randomUUID(),
        name: player.name.trim(),
        normalizedName,
        color: player.color,
        createdAt: now,
        updatedAt: now,
      };
      profiles.push(profile);
      profileStore.put(profile);
    }
    profileIds[player.id] = profile.id;
  }

  const archived: ArchivedMatch = {
    id,
    game,
    profileIds,
    archivedAt: now,
    ...links,
  };
  matchStore.put(archived);
  await transactionDone(transaction);
  database.close();
  return archived;
}

export async function loadArchivedMatches(): Promise<ArchivedMatch[]> {
  const database = await openBullseyeDb();
  const matches = await requestValue(
    database.transaction("matches", "readonly").objectStore("matches").getAll(),
  ) as ArchivedMatch[];
  database.close();
  return matches.sort((a, b) => b.archivedAt - a.archivedAt);
}

export async function loadProfiles(): Promise<LocalProfile[]> {
  const database = await openBullseyeDb();
  const profiles = await requestValue(
    database.transaction("profiles", "readonly").objectStore("profiles").getAll(),
  ) as LocalProfile[];
  database.close();
  return profiles.filter((profile) => !profile.archived).sort((a, b) => a.name.localeCompare(b.name));
}

export async function deleteArchivedMatch(id: string): Promise<void> {
  const database = await openBullseyeDb();
  const transaction = database.transaction("matches", "readwrite");
  transaction.objectStore("matches").delete(id);
  await transactionDone(transaction);
  database.close();
}

export async function migrateLegacyHistory(): Promise<void> {
  const database = await openBullseyeDb();
  const metadata = database.transaction("metadata", "readonly").objectStore("metadata");
  const migrated = await requestValue(metadata.get("legacy-history-migrated"));
  database.close();
  if (migrated) return;

  try {
    const parsed = JSON.parse(localStorage.getItem(LEGACY_HISTORY_KEY) ?? "[]") as unknown[];
    for (const value of parsed) {
      const game = migrateGame(value);
      if (game?.status === "finished") await archiveCompletedMatch(game);
    }
  } finally {
    const db = await openBullseyeDb();
    const transaction = db.transaction("metadata", "readwrite");
    transaction.objectStore("metadata").put({ key: "legacy-history-migrated", at: Date.now() });
    await transactionDone(transaction);
    db.close();
  }
}

export async function exportLocalBackup(activeGame: GameState): Promise<void> {
  const [matches, profiles] = await Promise.all([loadArchivedMatches(), loadProfiles()]);
  const database = await openBullseyeDb();
  const tournaments = await requestValue(
    database.transaction("tournaments", "readonly").objectStore("tournaments").getAll(),
  );
  database.close();
  const backup = { version: 1, exportedAt: Date.now(), activeGame, profiles, matches, tournaments };
  const url = URL.createObjectURL(new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `bullseye-backup-${new Date().toISOString().slice(0, 10)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function importLocalBackup(value: unknown): Promise<GameState | null> {
  if (!value || typeof value !== "object") return null;
  const backup = value as {
    version?: unknown;
    activeGame?: unknown;
    profiles?: unknown;
    matches?: unknown;
    tournaments?: unknown;
  };
  if (
    backup.version !== 1 ||
    !Array.isArray(backup.profiles) ||
    !Array.isArray(backup.matches) ||
    !Array.isArray(backup.tournaments)
  ) return null;
  const activeGame = migrateGame(backup.activeGame);
  if (!activeGame) return null;
  const database = await openBullseyeDb();
  const transaction = database.transaction(["profiles", "matches", "tournaments"], "readwrite");
  const profileStore = transaction.objectStore("profiles");
  const matchStore = transaction.objectStore("matches");
  const tournamentStore = transaction.objectStore("tournaments");
  profileStore.clear();
  matchStore.clear();
  tournamentStore.clear();
  (backup.profiles as LocalProfile[]).forEach((profile) => profileStore.put(profile));
  (backup.matches as ArchivedMatch[]).forEach((match) => matchStore.put(match));
  (backup.tournaments as Tournament[]).forEach((tournament) => tournamentStore.put(tournament));
  await transactionDone(transaction);
  database.close();
  return activeGame;
}

export async function loadTournaments(): Promise<Tournament[]> {
  const database = await openBullseyeDb();
  const tournaments = await requestValue(
    database.transaction("tournaments", "readonly").objectStore("tournaments").getAll(),
  ) as Tournament[];
  database.close();
  return tournaments.sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function saveTournament(tournament: Tournament): Promise<void> {
  const database = await openBullseyeDb();
  const transaction = database.transaction("tournaments", "readwrite");
  transaction.objectStore("tournaments").put(tournament);
  await transactionDone(transaction);
  database.close();
}
